import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Caption1,
  Field,
  MessageBar,
  MessageBarBody,
  Select,
  makeStyles,
} from '@fluentui/react-components';
import {
  maskDateOfBirth,
  maskSsn,
  revealPurposeSchema,
  type RevealPurpose,
  type SensitiveField,
} from '@vigeo/shared';
import { tokens } from '@vigeo/ui';
import { fetchSensitiveOnFile, revealSensitive, type ApiError } from '@/lib/api';

/**
 * The masked Social Security number and date of birth on a person's record (ADR-012 §4, #203).
 *
 * THE REVEALED VALUE LIVES IN COMPONENT STATE AND NOWHERE ELSE. It is never a `useQuery`, never
 * written to storage, never put in a URL. A query would put a Social Security number in the
 * TanStack cache, where it outlives this screen, survives navigation, and is readable by anything
 * else that asks the cache for that key — which is the whole reason the reveal is a POST with
 * `no-store` on the server side. A test asserts the cache stays empty of it.
 *
 * It re-masks after sixty seconds and on unmount, so walking away from a desk is not the same as
 * leaving the number on screen.
 */

/** ADR-012 §4. Long enough to read and type, short enough that a walk-away does not linger. */
const REVEAL_SECONDS = 60;

const PURPOSE_LABELS: Record<RevealPurpose, string> = {
  'background-check': 'Ordering a background check',
  'sworn-disclosure-correction': 'Correcting the sworn disclosure',
  'payroll-setup': 'Setting up payroll',
  'identity-correction': 'Correcting an identity detail',
  other: 'Another reason',
};

const useStyles = makeStyles({
  group: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM, maxWidth: '28rem' },
  row: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS, flexWrap: 'wrap' },
  /** Tabular figures so a revealed number does not reflow the row it sits in. */
  value: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase300 },
  ask: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
});

export interface SensitiveIdentityFieldsProps {
  readonly personId: string;
  /** False for a viewer who may not reveal. The server refuses regardless; this hides the control. */
  readonly canReveal: boolean;
}

export function SensitiveIdentityFields({ personId, canReveal }: SensitiveIdentityFieldsProps) {
  const styles = useStyles();
  const onFile = useQuery({
    queryKey: ['sensitive-on-file', personId],
    queryFn: () => fetchSensitiveOnFile(personId),
    // Safe to cache: two booleans and four digits, which is what the endpoint is FOR. The reveal
    // is the one that must not be cached, and it is not a query at all.
  });

  // 404 is what a deployment with the feature switched off answers, and what a person with no
  // record answers. Either way there is nothing to show and nothing to say about it.
  if (onFile.isError || onFile.data === undefined) return null;

  return (
    <div className={styles.group}>
      <SensitiveValue
        personId={personId}
        field="ssn"
        label="Social Security number"
        masked={onFile.data.ssnLast4 === null ? null : maskSsn(onFile.data.ssnLast4)}
        canReveal={canReveal}
      />
      <SensitiveValue
        personId={personId}
        field="dateOfBirth"
        label="Date of birth"
        masked={onFile.data.dateOfBirthOnFile ? maskDateOfBirth() : null}
        canReveal={canReveal}
      />
    </div>
  );
}

interface SensitiveValueProps {
  readonly personId: string;
  readonly field: SensitiveField;
  readonly label: string;
  /** The masked rendering, or null when nothing is on file. */
  readonly masked: string | null;
  readonly canReveal: boolean;
}

function SensitiveValue({ personId, field, label, masked, canReveal }: SensitiveValueProps) {
  const styles = useStyles();
  const [asking, setAsking] = useState(false);
  const [purpose, setPurpose] = useState<RevealPurpose | ''>('');
  const [revealed, setRevealed] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const reasonField = useRef<HTMLSelectElement>(null);
  // The question starts the keyboard on its first field, the reason (round 2: a form that already asks for a reason is the ask).
  useEffect(() => { if (asking) reasonField.current?.focus(); }, [asking]);

  const hide = useCallback(() => {
    setRevealed(null);
    if (timer.current !== null) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  // UNMOUNT RE-MASKS. Navigating away from the record must not leave the value in a component
  // React is keeping around, and must not leave a timer holding a closure over it.
  useEffect(() => hide, [hide]);

  if (masked === null) {
    return (
      <Field label={label}>
        <Caption1>Not on file.</Caption1>
      </Field>
    );
  }

  // Closing the question without a reveal forgets the reason picked: the next ask starts empty, so a reveal cannot be recorded under a
  // reason that was chosen last time. (A successful reveal clears it in `ask`.)
  const closeQuestion = () => { setAsking(false); setPurpose(''); };

  const ask = async () => {
    const parsed = revealPurposeSchema.safeParse(purpose);
    if (!parsed.success) return;
    setBusy(true);
    setError(null);
    try {
      const response = await revealSensitive(personId, { field, purpose: parsed.data });
      setRevealed(response.value);
      setAsking(false);
      setPurpose('');
      timer.current = setTimeout(hide, REVEAL_SECONDS * 1000);
    } catch (caught) {
      // The SERVER's words, not ours: it knows whether this was a rate limit or an outage, and
      // `status` is there so nobody has to match on the prose to find out.
      const api = caught as Error & Partial<ApiError>;
      setError(api.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Field label={label}>
      <div className={styles.row}>
        <span className={styles.value}>{revealed ?? masked}</span>
        {!canReveal ? null : revealed !== null ? (
          <Button size="small" onClick={hide}>Hide</Button>
        ) : (
          <Button size="small" onClick={() => (asking ? closeQuestion() : setAsking(true))} aria-expanded={asking}>
            Reveal
          </Button>
        )}
      </div>

      {revealed === null ? null : <Caption1>Hides itself after {REVEAL_SECONDS} seconds.</Caption1>}

      {!asking || revealed !== null ? null : (
        <div className={styles.ask}>
          {/* A CLOSED list. Free text is where somebody types the number (ADR-012 §4). */}
          <Field label="Why are you revealing this?" required>
            <Select ref={reasonField} value={purpose} onChange={(_, d) => setPurpose(d.value as RevealPurpose | '')}>
              <option value="">Choose a reason</option>
              {revealPurposeSchema.options.map((option) => (
                <option key={option} value={option}>{PURPOSE_LABELS[option]}</option>
              ))}
            </Select>
          </Field>
          <Caption1>This is recorded on the person&rsquo;s History.</Caption1>
          <div className={styles.row}>
            {/* Cancel on the left, the one blue action on the right: the same order as every dialog (card 7, PG1). */}
            <Button size="small" onClick={closeQuestion}>Cancel</Button>
            {/*
              "Reveal and record", not "Reveal". It says what the button does — the view is written
              to this person's History before the value appears — and it gives the confirm a name
              distinct from the two "Reveal" toggles on this screen, which a test would otherwise
              have to pick between by position.
            */}
            <Button appearance="primary" size="small" disabled={purpose === '' || busy} onClick={ask}>
              Reveal and record
            </Button>
          </div>
        </div>
      )}

      {error === null ? null : (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      )}
    </Field>
  );
}
